import React, { ReactElement } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Mention } from '@ckeditor/ckeditor5-mention';
import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Bold, Italic, Underline } from '@ckeditor/ckeditor5-basic-styles';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Link } from '@ckeditor/ckeditor5-link';
import { List } from '@ckeditor/ckeditor5-list';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import MentionItem from './mention-item/mention-item.component';
import HashtagItem from './hashtag-item/hashtag-item.component';
import MentionUserLinks from './mention-user-links/mention-user-links.plugin';
import FormatService from '../../services/format-service';
import ScreenService from '../../services/screen-service';
import useMobileDevice from '../../hooks/use-mobile-device';
import { SIGN_HASH, SIGN_AT } from '../../constants/system/signs';
import { KEYBOARD_KEY_ENTER } from '../../constants/system/keyboard-keys';
import {
  HTML_TAG_NAME_HEADER, HTML_TAG_NAME_SPAN, HTML_TAG_NAME_SUBHEADER,
} from '../../constants/system/html-tag-names';
import HTTP_PROTOCOL_HTTPS from '../../constants/system/http-protocols';
import 'ckeditor5/ckeditor5.css';
import './rich-text-editor.styles.scss';

const RichTextEditor: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const LANGUAGE_DEFAULT: string = 'en';
  const DROPDOWN_LIMIT_DEFAULT: number = 10;
  const MIN_CHARACTERS_DEFAULT: number = 0;
  const MODEL_PARAGRAPH: string = 'paragraph';
  const MODEL_HEADER: string = 'heading1';
  const MODEL_SUBHEADER: string = 'heading2';
  const CLASS_NAME_PARAGRAPH: string = 'ck-heading_paragraph';
  const CLASS_NAME_HEADER: string = 'ck-heading_heading1';
  const CLASS_NAME_SUBHEADER: string = 'ck-heading_heading2';

  const {
    isSimple,
    withMentions,
    withHashtags,
    withAutoFocus,
    withSendByEnter,
    value,
    mentionRouteRoot,
    hashtagRouteRoot,
    className,
    mentionList,
    hashtagList,
    onFieldChange,
    onKeyDown,
    ...custom
  } = props;

  const isMobileDevice: boolean = useMobileDevice();

  const keyDown: Function = (event: React.KeyboardEvent, data: CKEditorData): void => {
    if (onKeyDown && event) {
      onKeyDown(event, data);
    }
  };

  const fieldChange: Function = (event: React.ChangeEvent, data: CKEditorData): void => {
    if (onFieldChange) {
      onFieldChange(event, data);
    }
  };

  return (
    <CKEditor
      disableWatchdog
      editor={ClassicEditor}
      config={{
        toolbar: {
          items: [
            'bold',
            'italic',
            'underline',
            '|',
            'bulletedList',
            'numberedList',
            ...(
              !isMobileDevice ? [
                '|',
                'link',
                '|',
                'heading',
                '|',
                'undo',
                'redo',
              ] : []
            ),
          ],
        },
        heading: {
          options: !isSimple ? [
            {
              model: MODEL_PARAGRAPH,
              title: 'Paragraph',
              class: CLASS_NAME_PARAGRAPH,
            },
            {
              model: MODEL_HEADER,
              view: HTML_TAG_NAME_HEADER,
              title: 'Heading 1',
              class: CLASS_NAME_HEADER,
            },
            {
              model: MODEL_SUBHEADER,
              view: HTML_TAG_NAME_SUBHEADER,
              title: 'Heading 2',
              class: CLASS_NAME_SUBHEADER,
            },
          ] : [],
        },
        link: {
          defaultProtocol: HTTP_PROTOCOL_HTTPS,
          addTargetToExternalLinks: true,
        },
        mention: withMentions || withHashtags ? {
          feeds: [
            ...(withMentions ? [
              {
                marker: SIGN_AT,
                feed: mentionList || [],
                minimumCharacters: MIN_CHARACTERS_DEFAULT,
                dropdownLimit: DROPDOWN_LIMIT_DEFAULT,
                itemRenderer: (mentionItem: ViewItem): ChildNode | null => {
                  const mentionElement: HTMLElement | null = ScreenService.createElement(HTML_TAG_NAME_SPAN);

                  if (mentionElement) {
                    const mentionItemHtml: string | null = FormatService.formatComponentToHtml(
                      <MentionItem mentionItem={mentionItem} />,
                    );

                    if (mentionItemHtml) {
                      mentionElement.innerHTML = mentionItemHtml;
                    }

                    return mentionElement.firstChild;
                  }

                  return null;
                },
              },
            ] : []),
            ...(withHashtags ? [
              {
                marker: SIGN_HASH,
                feed: hashtagList || [],
                minimumCharacters: MIN_CHARACTERS_DEFAULT,
                dropdownLimit: DROPDOWN_LIMIT_DEFAULT,
                itemRenderer: (hashtagItem: ViewItem): ChildNode | null => {
                  const hashtagElement: HTMLElement | null = ScreenService.createElement(HTML_TAG_NAME_SPAN);

                  if (hashtagElement) {
                    const hashtagItemHtml: string | null = FormatService.formatComponentToHtml(
                      <HashtagItem hashtagItem={hashtagItem} />,
                    );

                    if (hashtagItemHtml) {
                      hashtagElement.innerHTML = hashtagItemHtml;
                    }

                    return hashtagElement.firstChild;
                  }

                  return null;
                },
              },
            ] : []),
          ],
        } : null,
        mentionUserLinks: {
          mentionRouteRoot,
          hashtagRouteRoot,
        },
        plugins: [
          Essentials,
          Bold,
          Italic,
          Underline,
          List,
          Mention,
          MentionUserLinks,
          Heading,
          Link,
          Paragraph,
        ],
        language: LANGUAGE_DEFAULT,
      }}
      data={value || ''}
      className={className}
      onReady={(editor: CKEditorInstance): void => {
        if (editor) {
          if (withAutoFocus) {
            editor.editing.view.focus();
          }

          editor.editing.view.document.on('keydown', (eventInfo: CKEditorEventInfo, event: DomEventData): void => {
            fieldChange(event.domEvent, editor.getData());
            keyDown(event.domEvent, editor.getData());

            if (withSendByEnter && event.domEvent.key === KEYBOARD_KEY_ENTER && !event.domEvent.shiftKey) {
              eventInfo.stop();
            }
          });

          editor.editing.view.document.on('change', (eventInfo: CKEditorEventInfo, event: DomEventData): void => {
            fieldChange(event.domEvent, editor.getData());
          });
        }
      }}
      {...custom}
    />
  );
});

export default RichTextEditor;
